import { useTranslation } from 'react-i18next';
import {
  BuildingColumnsIcon,
  CalendarClockIcon,
  ChartDoubleLineIcon,
  FileCertificateIcon,
  GrowFundsIcon,
  HourglassIcon,
  LightBulbGearIcon,
  PercentageDownIcon,
} from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import { useFinanceIqEnabled } from '@/enterprise/data/useFinanceIqEnabled';
import useGroupByBuyer, { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import { useIsLendingEligible } from '@/enterprise/features/lendingConnections/utils';
import { isTakerMarketEligibleForOffer } from '@/enterprise/features/offerModal/utils';
import useFeature from '@/lib/features';
import useExchangeRates from '@/shared/data/useExchangeRates';
import convertCurrency from '@/utils/convertCurrency';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { MarketingWidgetTemplateProps } from './MarketingWidgetTemplate';
import { renderLiquidityIconComponent } from './utils';

const useClientMarketingWidget: () => MarketingWidgetTemplateProps = () => {
  const { t, i18n } = useTranslation();
  const { data: takerMarkets } = useTakerMarkets();
  const makerOrgUuids = new Set(takerMarkets.map((tm) => tm.makerOrganizationUuid) ?? []);
  const buyers = useGroupByBuyer({ takerMarkets });
  const isLendingEligible = useIsLendingEligible();
  const isFinanceIQEnabled = useFinanceIqEnabled();
  const [enableQuarterEndReminder] = useFeature('enterprise-ui_enableQuarterEndReminder');
  const [enableFedRateCutWidget] = useFeature('enterprise-ui_fedRateBanner');
  const [enableLendingWidget] = useFeature('enterprise-ui_enableLendingWidget', {
    makerOrgUuids: Array.from(makerOrgUuids),
  });
  const isEnglishLanguageSelected = i18n.language === 'en';
  const { selectedCurrency } = useCurrencyContext();
  const { data: exchangeRates } = useExchangeRates();
  const { asCurrency } = useLocaleFormat();

  // Get the buyer with the highest eligible invoice amount that is eligible for an offer
  const getHighestEligibleBuyer = (buyers: BuyerGroupWithStats[]) => {
    if (!buyers?.length) {
      return null;
    }

    const eligibleBuyers = buyers
      .filter((buyer) => buyer.stats.totalInvoiceAmountUsd > 0) // filter out buyers with no invoice amount
      .map((buyer) => {
        // filter out markets that are not eligible for offer and update total eligible invoice amount
        const eligibleMarkets = buyer.takerMarkets
          .filter(isTakerMarketEligibleForOffer)
          .filter((market) => market.totalInvoiceAmountUsd > 0);
        const eligibleAmount = eligibleMarkets.reduce(
          (sum, market) =>
            sum +
            convertCurrency({
              amount: market.totalInvoiceAmount,
              from: market.currency,
              to: selectedCurrency,
              exchangeRates,
            }),
          0
        );

        return {
          ...buyer,
          eligibleAmount,
          highestTakerMarket: eligibleMarkets.sort((a, b) => b.totalInvoiceAmountUsd - a.totalInvoiceAmountUsd)[0],
        };
      })
      .filter((buyer) => buyer.eligibleAmount > 0);

    return eligibleBuyers.reduce(
      (highest, current) => (current.eligibleAmount > highest.eligibleAmount ? current : highest),
      eligibleBuyers[0]
    );
  };

  // Fed rate widget has the highest priority
  if (enableFedRateCutWidget) {
    return {
      icon: renderLiquidityIconComponent(PercentageDownIcon, 'percentage down'),
      title: t('banner.fedRates.title', 'The US Federal Reserve has lowered its benchmark rates.'),
      description: t('banner.fedRates.description', 'Lower interest rates make C2FO even more affordable.'),
      buttonText: t('banner.fedRates.actionText', 'Learn More'),
      buttonLink:
        'https://c2fo.com/resources/working-capital/federal-reserve-cuts-interest-rates-in-september-2025-amid-economic-pressures/?utm_partner=C2FO',
      isExternalLink: true,
      trackingMetadata: {
        location: 'homepage',
        source: 'client',
        widgetType: 'fed_rate_cut',
      },
    };
  }

  // Show quarter end opportunity if conditions are met:
  // 1. English language is selected
  // 2. Quarter end reminder feature flag is enabled
  // 3. There is at least one buyer with invoices eligible for an offer
  if (isEnglishLanguageSelected && enableQuarterEndReminder) {
    const highestBuyer = getHighestEligibleBuyer(buyers);
    if (highestBuyer?.highestTakerMarket) {
      const buyerDetailsLink = `customers/${highestBuyer.makerOrganizationUuid}/markets/${highestBuyer.highestTakerMarket.marketId}/division/${highestBuyer.highestTakerMarket.takerDivisionId}/invoices/eligible`;

      const invoiceAmount = asCurrency(highestBuyer.eligibleAmount, selectedCurrency);

      return {
        icon: renderLiquidityIconComponent(CalendarClockIcon, 'calendar time'),
        title: 'Last chance to submit your early payment request before 2025 year-end closure.',
        description: `You have ${invoiceAmount} available for early payment from ${highestBuyer.makerOrganizationName}.`,
        buttonLink: buyerDetailsLink,
        buttonText: 'Request Early Payment',
        trackingMetadata: {
          location: 'homepage',
          source: 'client',
          widgetType: 'quarter_end_opportunity',
          eligibleInvoiceAmountUsd: highestBuyer.eligibleAmount,
          makerOrganizationName: highestBuyer.makerOrganizationName,
          makerOrganizationUuid: highestBuyer.makerOrganizationUuid,
        },
      };

      // Q3 Version
      //  return {
      //   icon: renderLiquidityIconComponent(CalendarClockIcon, 'calendar time'),
      //   title: 'Boost Q3 results - meet your working capital needs today.',
      //   description: `Access debt-free cash flow by accelerating ${highestBuyer.makerOrganizationName} invoice payment.`,
      //   buttonLink: buyerDetailsLink,
      //   buttonText: 'Get Started',
      //   trackingMetadata: {
      //     location: 'homepage',
      //     widgetType: 'quarter_end_opportunity',
      //     eligibleInvoiceAmountUsd: highestBuyer.eligibleAmount,
      //     makerOrganizationName: highestBuyer.makerOrganizationName,
      //     makerOrganizationUuid: highestBuyer.makerOrganizationUuid,
      //   },
      // };
    }
  }

  if (enableLendingWidget && isLendingEligible) {
    return {
      icon: renderLiquidityIconComponent(GrowFundsIcon, 'grow funds'),
      title: 'Beyond Early Payment',
      description: 'Discover more funding solutions that meet your business needs through C2FO Lending Connections.',
      buttonText: 'Learn More',
      buttonLink: 'lending',
      trackingMetadata: {
        location: 'homepage',
        source: 'client',
        widgetType: 'lending_connections',
      },
    };
  }

  if (isFinanceIQEnabled) {
    return {
      icon: renderLiquidityIconComponent(ChartDoubleLineIcon, 'line chart'),
      title: 'New in C2FO FinanceIQ: See your full AR portfolio in one place',
      description:
        'Spend less time reconciling receivables. The new Receivables Manager consolidates approvals, overdue invoices, and deductions across customers.',
      buttonText: 'Explore Now',
      buttonLink:
        window.location.origin +
        '/financeiq/?utm_partner=&utm_lang=en_US&utm_source=c2fo&utm_medium=in_app&utm_region=amer&utm_lifecycle=cross&utm_audience=na&utm_campaign=financeiq&utm_series=ent-widget426&utm_country=us',
      isExternalLink: true,
      trackingMetadata: {
        location: 'homepage',
        source: 'client',
        widgetType: 'financeiq',
      },
    };
  }

  // default to how it works
  return {
    icon: renderLiquidityIconComponent(LightBulbGearIcon, 'light bulb gear'),
    title: t('marketWidget.howItWorks.title', 'How it works'),
    description: (
      <div className="space-y-4">
        {[
          {
            icon: FileCertificateIcon,
            iconAlt: 'file certificate',
            title: t('marketWidget.howItWorks.setYourOffer.title', 'Set Your Offer'),
            description: t('marketWidget.howItWorks.setYourOffer.description'),
          },
          {
            icon: HourglassIcon,
            iconAlt: 'hour glass',
            title: t('marketWidget.howItWorks.offerEvaluated.title', 'Offer Evaluated'),
            description: t('marketWidget.howItWorks.offerEvaluated.description'),
          },
          {
            icon: BuildingColumnsIcon,
            iconAlt: 'building columns',
            title: t('marketWidget.howItWorks.paymentInitiated.title', 'Payment Initiated'),
            description: t('marketWidget.howItWorks.paymentInitiated.description'),
          },
        ].map((step) => {
          const IconComponent = step.icon;

          return (
            <div key={step.title} className="flex gap-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white">
                <IconComponent className="size-6 fill-text-secondary" />
              </div>
              <div>
                <div>{step.title}</div>
                <p className="text-sm font-normal">{step.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    ),
    trackingMetadata: {
      location: 'homepage',
      source: 'client',
      widgetType: 'how_it_works',
    },
  };
};

export default useClientMarketingWidget;
